import { useTheme } from '@shopify/restyle';

import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import type { ImagePickerAsset, ImagePickerSuccessResult } from 'expo-image-picker';
import type { FieldError } from 'react-hook-form';
import { IconCamera, IconPencil } from '@tabler/icons-react-native';
import { Column, Row } from './box';
import { Button } from './button';
import { FormError } from './form-error';
import { PCImage } from './image';
import { Text } from './text';
import { Pressable } from 'react-native';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import type { Theme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

interface ImagePickerProps {
  error?: FieldError;
  onChange: (result: ImagePickerSuccessResult) => void;
  subtitle?: string;
  title: string;
  value?: ImagePickerSuccessResult | ImagePickerAsset | null;
}

export function ImagePicker({ error, onChange, subtitle, title, value }: ImagePickerProps) {
  const theme = useTheme<Theme>();

  const { onUseImagePickerCamera, settingsPromptState } = useImagePickerCamera(onChange, undefined, {
    allowsMultipleSelection: false,
  });

  const imageAsset = !value ? undefined : 'assets' in value ? value.assets[0] : value;
  const pictureUri = imageAsset?.uri;

  const pickerHeight = 140;

  return (
    <>
      <Column gap="spacing.general.x2">
        <Row flex={1} height={pickerHeight} justifyContent={pictureUri ? 'center' : undefined}>
          {pictureUri ? (
            <Pressable onPress={() => onUseImagePickerCamera()} style={{ overflow: 'hidden' }}>
              <PCImage
                aspectRatio={1}
                height={pickerHeight}
                source={{ uri: pictureUri }}
                style={{ borderRadius: theme.borderRadii['radius.lg'] }}
              />

              <Column
                alignItems="center"
                aspectRatio={1}
                backgroundColor="surface.inverse.tertiary-neutral"
                borderRadius="radius.xl"
                height={30}
                justifyContent="center"
                position="absolute"
                right={0}
                top={0}
              >
                <IconPencil strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.primary']} size={20} />
              </Column>
            </Pressable>
          ) : (
            <Button
              alignItems="center"
              flex={1}
              flexDirection="column"
              height={pickerHeight}
              justifyContent="center"
              onPress={() => onUseImagePickerCamera()}
              padding="spacing.general.x8"
              style={{
                borderStyle: 'dashed',
                borderColor: theme.colors['surface.inverse.tertiary-neutral'],
              }}
              variant="secondary"
            >
              <IconCamera strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.secondary']} size={24} />

              <Text color="text.primary" fontWeight="700" textAlign="center" variant="bodyBase">
                {title}
              </Text>

              {subtitle ? (
                <Text color="text.primary" marginTop="spacing.general.x0,5" textAlign="center" variant="bodyBase">
                  {subtitle}
                </Text>
              ) : null}
            </Button>
          )}
        </Row>

        {error ? <FormError {...{ error }} /> : null}
      </Column>
      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </>
  );
}
